import React from 'react'
import { useFormFactor, useGroup } from '../../../hooks'
import { useDisplayAddToPlaylist } from '../../../hooks/useDisplayAddToPlaylist'
import {
  AddToPlaylistButton,
  BookmarkButton,
  MoreActions,
  ToggleDescriptionButton
} from '../../action-buttons'
import { ALLOW_PLAYLISTS_GROUP } from '../../user-group-policy/permissions'
import { useCardExpand } from '../hooks'
import BaseContentCard from './base-content-card'

/** Card for displaying content such as articles, videos and audio etc */
const ContentCard = ({
  // Destructured out so a caller-supplied `expand` can't flow through
  // `{...props}` and override the hook-driven `expand={expanded}` below.
  expand,
  onAddToPlaylist,
  onBookmark,
  bookmarked,
  actionLoading,
  ...props
}) => {
  const { isDesktop } = useFormFactor()
  const displayAddToPlaylist = useDisplayAddToPlaylist()

  const {
    expanded,
    hoverShadow,
    handleToggleDescription,
    handleMouseEnter,
    handleMouseLeave
  } = useCardExpand()

  const { isInGroup } = useGroup()

  const canAddToPlaylist =
    isInGroup(ALLOW_PLAYLISTS_GROUP) && displayAddToPlaylist

  const cardActions = [
    {
      key: 'Bookmark',
      inline: (
        <BookmarkButton
          size='xs'
          variant='secondary'
          onClick={onBookmark}
          bookmarked={bookmarked}
          loading={actionLoading}
          aria-label='Bookmark this item'
        />
      ),
      menuItem: ({ onClose, className }) => (
        <BookmarkButton
          key='Bookmark'
          className={className}
          onClick={() => {
            onBookmark()
            onClose()
          }}
          bookmarked={bookmarked}
          aria-label='Bookmark this item'
        />
      )
    },
    ...(canAddToPlaylist
      ? [{
          key: 'AddToPlaylist',
          inline: (
            <AddToPlaylistButton
              size='xs'
              variant='secondary'
              onClick={() => onAddToPlaylist(props.content)}
              aria-label='Add this item to a playlist'
            />
          ),
          menuItem: ({ onClose, className }) => (
            <AddToPlaylistButton
              key='AddToPlaylist'
              className={className}
              onClick={() => {
                onAddToPlaylist(props.content)
                onClose()
              }}
              aria-label='Add this item to a playlist'
            />
          )
        }]
      : [])
  ]

  // MT.3 action-visibility rule: ≤2 actions render inline as pill buttons
  // (matching the secondary variant the old "Actions" trigger used). >2
  // actions show the first inline and collapse the rest into MoreActions,
  // so the action row stays compact regardless of how many actions are
  // wired up.
  const renderActions = () => {
    if (cardActions.length <= 2) {
      return cardActions.map((a) => (
        <React.Fragment key={a.key}>{a.inline}</React.Fragment>
      ))
    }
    const [first, ...rest] = cardActions
    return (
      <>
        <React.Fragment key={first.key}>{first.inline}</React.Fragment>
        <MoreActions
          size='xs'
          loading={actionLoading}
          actions={(actions) => rest.map((a) => a.menuItem(actions))}
        />
      </>
    )
  }

  const actions = {
    leftActions: (
      <>
        {!isDesktop && (
          <ToggleDescriptionButton
            size='xs'
            toggled={expanded}
            onClick={handleToggleDescription}
          />
        )}
        {renderActions()}
      </>
    )
  }
  return (
    <BaseContentCard
      actions={actions}
      expand={expanded}
      hoverShadow={hoverShadow}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    />
  )
}

ContentCard.defaultProps = {
  size: 'small'
}
export default ContentCard
